import React, { useState, useEffect, useRef, useMemo } from 'react';
import { FaSearch, FaChevronDown, FaChevronUp, FaEdit, FaImage } from 'react-icons/fa';
import Swal from 'sweetalert2';
import api, { BASE_URL } from '../../../utils/api';
import { useAuth } from '../../../context/AuthContext';
import './Viewcollege.css';

const API_BASE = BASE_URL;

// Map backend fields → form fields
const toForm = (col) => ({
  name:    col.name    || '',
  title:   col.titleHeader   || '',
  code:    col.code    || '',
  email:   col.email   || '',
  contact: col.contactNumber || '',
  address: col.address || '',
  terms:   col.termCondition || '',
  collegeId:       col.collegeId || '',
  collegePassword: '',
  clerkName:       col.clerkName || '',
  clerkId:         col.clerkId   || '',
  clerkPassword:   '',
  hasCollegePassword: col.hasCollegePassword || false,
  hasClerkPassword:   col.hasClerkPassword   || false,
});

const EMPTY_FORM = { 
  name: '', title: '', code: '', email: '', contact: '', address: '', terms: '',
  collegeId: '', collegePassword: '', clerkName: '', clerkId: '', clerkPassword: '',
  hasCollegePassword: false, hasClerkPassword: false,
};

const ViewCollege = () => {
  const { user } = useAuth();
  const [colleges, setColleges]             = useState([]);
  const [loading, setLoading]               = useState(false);
  const [saving, setSaving]                 = useState(false);
  const [searchQuery, setSearchQuery]       = useState('');
  const [showSearchDropdown, setShowSearchDropdown] = useState(false);
  const [showEditForm, setShowEditForm]     = useState(false);
  const [form, setForm]                     = useState(EMPTY_FORM);
  const [logoFile, setLogoFile]             = useState(null);
  const [previewLogo, setPreviewLogo]       = useState(null);
  const [editId, setEditId]                 = useState(null);
  const [expandedRow, setExpandedRow]       = useState(null);
  const [changeCollegePwd, setChangeCollegePwd] = useState(false);
  const [changeClerkPwd,   setChangeClerkPwd]   = useState(false);

  const searchRef = useRef(null);

  // ── Load colleges from backend ──────────────────────────────────────────────
  const loadColleges = async () => {
    setLoading(true);
    try {
      // Pass all=true so disabled colleges also show (admin needs to re-enable them)
      const { data } = await api.get('/controlpanel/admin/colleges', { params: { all: 'true', _t: Date.now() } });
      let all = data.data;

      // For CPUser (admin) — fetch fresh college assignments from server
      if (user?.role === 'admin') {
        try {
          const { data: adminData } = await api.get('/controlpanel/admin/me', { params: { _t: Date.now() } });
          if (adminData?.data?.colleges && adminData.data.colleges.length > 0) {
            // Preserve assignment order
            const orderedColleges = adminData.data.colleges.map(assignedCollege => 
              all.find(c => String(c._id) === String(assignedCollege._id || assignedCollege))
            ).filter(Boolean);
            all = orderedColleges;
          } else {
            all = [];
          }
        } catch {
          // Fallback to session data if API fails
          if (user?.colleges && user.colleges.length > 0) {
            const orderedColleges = user.colleges.map(assignedCollege => 
              all.find(c => String(c._id) === String(assignedCollege._id || assignedCollege))
            ).filter(Boolean);
            all = orderedColleges;
          } else {
            all = [];
          }
        }
      } else if (user?.college) {
        const myId = String(user.college._id || user.college);
        all = all.filter(c => String(c._id) === myId);
      }

      setColleges(all);
    } catch {
      Swal.fire('Error', 'Failed to load colleges.', 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadColleges(); }, []);

  // Close search dropdown on outside click
  useEffect(() => {
    const handler = (e) => {
      if (searchRef.current && !searchRef.current.contains(e.target))
        setShowSearchDropdown(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setForm(f => ({ ...f, [name]: value }));
  };

  const handleLogoChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setLogoFile(file);
    setPreviewLogo(URL.createObjectURL(file));
  };

  const validateForm = () => {
    const required = ['email', 'contact', 'address', 'clerkId'];
    if (required.some(f => !form[f])) {
      Swal.fire('Missing Fields', 'Please fill all required fields (including Clerk ID)!', 'warning');
      return false;
    }
    const emailReg = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailReg.test(form.email)) {
      Swal.fire('Invalid Email', 'Please enter a valid college email address.', 'warning');
      return false;
    }
    if (!emailReg.test(form.clerkId)) {
      Swal.fire('Invalid Clerk Email', 'Clerk Login ID must be a valid email address.', 'warning');
      return false;
    }
    if (form.collegeId && !emailReg.test(form.collegeId)) {
      Swal.fire('Invalid College Email', 'College Login ID must be a valid email address.', 'warning');
      return false;
    }
    if (changeClerkPwd && !form.clerkPassword) {
      Swal.fire('Missing Password', 'Please enter the new Clerk Password.', 'warning');
      return false;
    }
    if (changeCollegePwd && !form.collegePassword) {
      Swal.fire('Missing Password', 'Please enter the new College Password.', 'warning');
      return false;
    }
    return true;
  };

  const resetForm = () => {
    setForm(EMPTY_FORM);
    setLogoFile(null);
    setPreviewLogo(null);
    setEditId(null);
    setShowEditForm(false);
    setChangeCollegePwd(false);
    setChangeClerkPwd(false);
  };

  // ── Save (Update only) ──────────────────────────────────────────────────
  const handleSubmit = async () => {
    if (!validateForm() || !editId) return;
    setSaving(true);
    try {
      const fd = new FormData();
      fd.append('name',          form.name);
      fd.append('titleHeader',   form.title);
      fd.append('code',          form.code.toUpperCase());
      fd.append('email',         form.email);
      fd.append('contactNumber', form.contact);
      fd.append('address',       form.address);
      fd.append('termCondition', form.terms);
      fd.append('collegeId',     form.collegeId);
      fd.append('clerkName',     form.clerkName);
      fd.append('clerkId',       form.clerkId);
      // Only send passwords if admin explicitly chose to change them
      if (form.collegePassword) fd.append('collegePassword', form.collegePassword);
      if (form.clerkPassword)   fd.append('clerkPassword',   form.clerkPassword);

      if (logoFile) fd.append('logo', logoFile);

      const opts = { headers: { 'Content-Type': 'multipart/form-data' } };

      const { data } = await api.put(`/controlpanel/admin/colleges/${editId}`, fd, opts);
      setColleges(prev => prev.map(c => c._id === editId ? data.data : c));
      Swal.fire('Updated!', 'College information has been updated.', 'success');
      
      resetForm();
    } catch (err) {
      Swal.fire('Error', err.response?.data?.message || 'Failed to update college.', 'error');
    } finally {
      setSaving(false);
    }
  };

  // ── Edit ─────────────────────────────────────────────────────────────────────
  const handleEdit = (col) => {
    setEditId(col._id);
    setForm(toForm(col));
    setLogoFile(null);
    setPreviewLogo(col.logo ? `${API_BASE}/${col.logo}` : null);
    setShowEditForm(true);
    setExpandedRow(null);
    setChangeCollegePwd(false);
    setChangeClerkPwd(false);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // ── Toggle Active/Disabled ───────────────────────────────────────────────────
  const toggleStatus = async (col) => {
    const action = col.isActive ? 'disable' : 'enable';
    const result = await Swal.fire({
      title: `${col.isActive ? 'Disable' : 'Enable'} "${col.name}"?`,
      text: col.isActive
        ? 'Clerk and College Admin will not be able to login. Related data will be hidden.'
        : 'College will be re-enabled. All data will be visible again.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: col.isActive ? '#e74c3c' : '#27ae60',
      confirmButtonText: col.isActive ? 'Yes, Disable' : 'Yes, Enable',
    });
    if (!result.isConfirmed) return;
    try {
      const { data } = await api.patch(`/controlpanel/admin/colleges/${col._id}/toggle`);
      setColleges(prev => prev.map(c => c._id === col._id ? { ...c, isActive: data.data.isActive } : c));
      Swal.fire(col.isActive ? 'Disabled!' : 'Enabled!', `College has been ${action}d.`, 'success');
    } catch (err) {
      Swal.fire('Error', err.response?.data?.message || 'Failed to update status.', 'error');
    }
  };

  // ── Logo URL helper ──────────────────────────────────────────────────────────
  const getLogoUrl = (col) => col.logo ? `${API_BASE}/${col.logo}` : null;

  // ── Search filter ────────────────────────────────────────────────────────────
  const displayedColleges = useMemo(() => {
    if (!searchQuery) return colleges;
    const q = searchQuery.toLowerCase();
    return colleges.filter(c =>
      c.name.toLowerCase().includes(q) || c.code.toLowerCase().includes(q)
    );
  }, [colleges, searchQuery]);

  return (
    <div className="admin-panel-view-college-page-container">
      <h2 className="admin-panel-view-college-page-title">View Colleges</h2>

      {/* ── Controls ── */}
      <div className="admin-panel-view-college-page-controls">
        <div className="admin-panel-view-college-page-search-wrapper" ref={searchRef}>
          <FaSearch className="admin-panel-view-college-page-search-icon" />
          <input
            type="text"
            className="admin-panel-view-college-page-search-input"
            placeholder="Search by college name or code..."
            value={searchQuery}
            onChange={(e) => { setSearchQuery(e.target.value); setShowSearchDropdown(true); }}
            onFocus={() => setShowSearchDropdown(true)}
          />
          {showSearchDropdown && searchQuery && (
            <div className="admin-panel-view-college-page-search-dropdown">
              <ul>
                {displayedColleges.length > 0 ? displayedColleges.map(c => (
                  <li key={c._id} onClick={() => { setSearchQuery(c.name); setShowSearchDropdown(false); }}>
                    {c.name} ({c.code})
                  </li>
                )) : (
                  <div className="admin-panel-view-college-page-search-empty">No colleges found</div>
                )}
              </ul>
            </div>
          )}
        </div>
      </div>

      {/* ── Edit Form ── */}
      {showEditForm && (
        <div className="admin-panel-view-college-page-inline-form">
          <h3 className="admin-panel-view-college-page-form-title">
            ✏️ Edit College Info
          </h3>

          {/* 1. Logo Upload (Editable) */}
          <div className="admin-panel-view-college-page-logo-upload-wrapper">
            <div className="admin-panel-view-college-page-logo-preview">
              {previewLogo
                ? <img src={previewLogo} alt="Preview" />
                : <FaImage className="admin-panel-view-college-page-placeholder-icon" />}
            </div>
            <div className="admin-panel-view-college-page-upload-controls">
              <label className="admin-panel-view-college-page-upload-btn">
                {previewLogo ? 'Change Logo' : 'Upload Logo'}
                <input type="file" accept="image/*" onChange={handleLogoChange} style={{ display: 'none' }} />
              </label>
              <span className="admin-panel-view-college-page-upload-hint">JPG, PNG, WebP — max 5 MB</span>
              {previewLogo && (
                <button
                  type="button"
                  style={{ background: 'none', border: 'none', color: '#e74c3c', fontSize: '0.78rem', cursor: 'pointer', padding: 0, marginTop: 4 }}
                  onClick={() => { setLogoFile(null); setPreviewLogo(null); }}
                >
                  ✕ Remove logo
                </button>
              )}
            </div>
          </div>

          {/* 2. Core College Information */}
          <div className="admin-panel-view-college-page-form-grid">
            <div className="admin-panel-view-college-page-form-group">
              <label>College Name (Not Editable)</label>
              <input type="text" name="name" value={form.name} disabled className="admin-panel-view-college-page-disabled-input" />
            </div>
            <div className="admin-panel-view-college-page-form-group">
              <label>College Code <span className="admin-panel-view-college-page-req">*</span></label>
              <input type="text" name="code" value={form.code} onChange={e => setForm(f => ({ ...f, code: e.target.value.toUpperCase() }))} placeholder="e.g. UNI001" />
            </div>
            <div className="admin-panel-view-college-page-form-group">
              <label>College Title / Header <span style={{fontSize:'0.75rem',color:'#94a3b8',fontWeight:400}}>(optional)</span></label>
              <input type="text" name="title" value={form.title} onChange={handleInputChange} placeholder="e.g. Empowering Education" />
            </div>
            <div className="admin-panel-view-college-page-form-group">
              <label>Email Address <span className="admin-panel-view-college-page-req">*</span></label>
              <input type="email" name="email" value={form.email} onChange={handleInputChange} placeholder="admin@college.edu" />
            </div>
            <div className="admin-panel-view-college-page-form-group">
              <label>Contact Number <span className="admin-panel-view-college-page-req">*</span></label>
              <input type="text" name="contact" value={form.contact} onChange={handleInputChange} placeholder="Phone number" />
            </div>
            <div className="admin-panel-view-college-page-form-group">
              <label>Full Address <span className="admin-panel-view-college-page-req">*</span></label>
              <input type="text" name="address" value={form.address} onChange={handleInputChange} placeholder="Street, City, State, Zip" />
            </div>
          </div>

          <div className="admin-panel-view-college-page-form-group admin-panel-view-college-page-full-width" style={{ marginBottom: '24px' }}>
            <label>Terms and Conditions</label>
            <textarea name="terms" value={form.terms} onChange={handleInputChange} rows="3" placeholder="Enter specific terms and conditions for this college..." />
          </div>

          {/* 3. College Login (Editable) */}
          <h4 className="admin-panel-view-college-page-sub-title">College Login <span style={{fontSize:'0.75rem',color:'#94a3b8',fontWeight:400}}>(Optional)</span></h4>
          <div className="admin-panel-view-college-page-form-grid" style={{ marginBottom: '24px' }}>
            <div className="admin-panel-view-college-page-form-group">
              <label>College Login Email</label>
              <input type="email" name="collegeId" value={form.collegeId} onChange={handleInputChange} placeholder="college@example.com" />
            </div>
            <div className="admin-panel-view-college-page-form-group">
              <label>
                Password
                {changeCollegePwd && (
                  <span className="admin-panel-view-college-page-cancel-pwd"
                    onClick={() => { setChangeCollegePwd(false); setForm(f => ({ ...f, collegePassword: '' })); }}>
                    Cancel
                  </span>
                )}
              </label>
              {!changeCollegePwd && form.hasCollegePassword ? (
                <button type="button" className="admin-panel-view-college-page-btn-change-pwd"
                  onClick={() => setChangeCollegePwd(true)}>
                  Change Password
                </button>
              ) : (
                <input type="text" name="collegePassword" value={form.collegePassword}
                  onChange={handleInputChange} placeholder="Enter new password" />
              )}
            </div>
          </div>

          {/* 4. Clerk Login (Editable) */}
          <h4 className="admin-panel-view-college-page-sub-title">Clerk Login <span className="admin-panel-view-college-page-req">*</span></h4>
          <div className="admin-panel-view-college-page-form-grid">
            <div className="admin-panel-view-college-page-form-group">
              <label>Clerk Name <span style={{fontSize:'0.75rem',color:'#94a3b8',fontWeight:400}}>(optional)</span></label>
              <input type="text" name="clerkName" value={form.clerkName} onChange={handleInputChange} placeholder="Enter Clerk Name" />
            </div>
            <div className="admin-panel-view-college-page-form-group">
              <label>Clerk Login Email <span className="admin-panel-view-college-page-req">*</span></label>
              <input type="email" name="clerkId" value={form.clerkId} onChange={handleInputChange} placeholder="clerk@example.com" />
            </div>
            <div className="admin-panel-view-college-page-form-group">
              <label>
                Password <span className="admin-panel-view-college-page-req">*</span>
                {changeClerkPwd && (
                  <span className="admin-panel-view-college-page-cancel-pwd"
                    onClick={() => { setChangeClerkPwd(false); setForm(f => ({ ...f, clerkPassword: '' })); }}>
                    Cancel
                  </span>
                )}
              </label>
              {!changeClerkPwd && form.hasClerkPassword ? (
                <button type="button" className="admin-panel-view-college-page-btn-change-pwd"
                  onClick={() => setChangeClerkPwd(true)}>
                  Change Password
                </button>
              ) : (
                <input type="text" name="clerkPassword" value={form.clerkPassword}
                  onChange={handleInputChange} placeholder="Enter new password" />
              )}
            </div>
          </div>

          {/* Action Buttons */}
          <div className="admin-panel-view-college-page-form-buttons">
            <button className="admin-panel-view-college-page-btn-cancel" onClick={resetForm} disabled={saving}>Cancel</button>
            <button className="admin-panel-view-college-page-btn-confirm" onClick={handleSubmit} disabled={saving}>
              {saving ? 'Updating...' : 'Update College'}
            </button>
          </div>
        </div>
      )}

      {/* ── Table ── */}
      {loading ? (
        <div style={{ textAlign: 'center', padding: '40px', color: '#64748b' }}>Loading colleges...</div>
      ) : (
        <div className="admin-panel-view-college-page-table-wrapper">
          <table className="admin-panel-view-college-page-table">
            <thead>
              <tr>
                <th>S.No</th>
                <th className="admin-panel-view-college-page-align-left">College Name</th>
                <th className="admin-view-col-table-code">Code</th>
                <th className="admin-view-col-table-email">Email</th>
                <th className="admin-view-col-table-status">Status</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {displayedColleges.length === 0 ? (
                <tr><td colSpan="7" style={{ padding: '40px', textAlign: 'center', color: '#64748b' }}>No colleges found.</td></tr>
              ) : displayedColleges.map((col, i) => (
                <React.Fragment key={col._id}>
                  <tr className={`admin-panel-view-college-page-main-row${!col.isActive ? ' college-row-disabled' : ''}`}>
                    <td>{i + 1}</td>
                    <td className="admin-panel-view-college-page-align-left">
                      <div
                        className="admin-panel-view-college-page-name-wrapper"
                        onClick={() => setExpandedRow(expandedRow === col._id ? null : col._id)}
                        style={{ cursor: 'pointer' }}
                      >
                        <span>{col.name}</span>
                        <button className="admin-panel-view-college-page-expand-btn">
                          {expandedRow === col._id ? <FaChevronUp /> : <FaChevronDown />}
                        </button>
                      </div>
                    </td>
                    <td className="admin-view-col-table-code"><span className="admin-panel-view-college-page-badge-code">{col.code}</span></td>
                    <td className="admin-view-col-table-email">{col.email || '—'}</td>
                    <td className="admin-view-col-table-status">
                      <label className="admin-panel-view-college-page-switch">
                        <input type="checkbox" checked={col.isActive} onChange={() => toggleStatus(col)} />
                        <span className="admin-panel-view-college-page-slider" />
                      </label>
                      <div style={{ fontSize: '0.7rem', marginTop: '4px', color: col.isActive ? '#27ae60' : '#e74c3c', fontWeight: '600' }}>
                        {col.isActive ? 'Active' : 'Disabled'}
                      </div>
                    </td>
                    <td>
                      <div className="admin-panel-view-college-page-action-btns">
                        <button className="admin-panel-view-college-page-btn-edit" onClick={() => handleEdit(col)} title="Edit"><FaEdit /></button>
                      </div>
                    </td>
                  </tr>

                  {/* Expanded Row */}
                  {expandedRow === col._id && (
                    <tr className="admin-panel-view-college-page-expanded-row">
                      <td colSpan="6">
                        <div className="admin-panel-view-college-page-expanded-content">

                          {/* Logo — always in expand (not in table) */}
                          <div className="admin-panel-view-college-page-expanded-item">
                            <span className="admin-panel-view-college-page-exp-label">Logo</span>
                            <span className="admin-panel-view-college-page-exp-value">
                              {getLogoUrl(col)
                                ? <img
                                    src={getLogoUrl(col)}
                                    alt={col.name}
                                    style={{ width: 56, height: 56, borderRadius: 8, objectFit: 'cover', border: '1px solid #e2e8f0' }}
                                    onError={e => { e.target.style.display = 'none'; }}
                                  />
                                : <div className="admin-panel-view-college-page-logo-placeholder" style={{ width: 56, height: 56 }}>{col.name[0]}</div>}
                            </span>
                          </div>

                          {/* Title Header — always in expand (never in table) */}
                          <div className="admin-panel-view-college-page-expanded-item">
                            <span className="admin-panel-view-college-page-exp-label">Title Header</span>
                            <span className="admin-panel-view-college-page-exp-value">{col.titleHeader || '—'}</span>
                          </div>

                          {/* Contact — always in expand (never in table) */}
                          <div className="admin-panel-view-college-page-expanded-item">
                            <span className="admin-panel-view-college-page-exp-label">Contact</span>
                            <span className="admin-panel-view-college-page-exp-value">{col.contactNumber || '—'}</span>
                          </div>
                          
                          {/* Login Credentials Summaries */}
                          <div className="admin-panel-view-college-page-expanded-item">
                            <span className="admin-panel-view-college-page-exp-label">College Login Email</span>
                            <span className="admin-panel-view-college-page-exp-value">{col.collegeId || '—'}</span>
                          </div>
                          <div className="admin-panel-view-college-page-expanded-item">
                            <span className="admin-panel-view-college-page-exp-label">Clerk Login Email</span>
                            <span className="admin-panel-view-college-page-exp-value">{col.clerkId || '—'}</span>
                          </div>
                          <div className="admin-panel-view-college-page-expanded-item">
                            <span className="admin-panel-view-college-page-exp-label">Clerk Name</span>
                            <span className="admin-panel-view-college-page-exp-value">{col.clerkName || '—'}</span>
                          </div>

                          {/* Code — show in expand ONLY when hidden from table (< 480px) */}
                          <div className="admin-panel-view-college-page-expanded-item admin-view-col-exp-code">
                            <span className="admin-panel-view-college-page-exp-label">College Code</span>
                            <span className="admin-panel-view-college-page-exp-value">{col.code}</span>
                          </div>

                          {/* Email — show in expand ONLY when hidden from table (< 768px) */}
                          <div className="admin-panel-view-college-page-expanded-item admin-view-col-exp-email">
                            <span className="admin-panel-view-college-page-exp-label">Email</span>
                            <span className="admin-panel-view-college-page-exp-value">{col.email || '—'}</span>
                          </div>

                          {/* Status — show in expand ONLY when hidden from table (< 480px) */}
                          <div className="admin-panel-view-college-page-expanded-item admin-view-col-exp-status">
                            <span className="admin-panel-view-college-page-exp-label">Status</span>
                            <span className="admin-panel-view-college-page-exp-value" style={{ color: col.isActive ? '#27ae60' : '#e74c3c', fontWeight: 700 }}>
                              {col.isActive ? 'Active' : 'Disabled'}
                            </span>
                          </div>

                          {/* Address — always in expand (never in table) */}
                          <div className="admin-panel-view-college-page-expanded-item admin-panel-view-college-page-exp-half">
                            <span className="admin-panel-view-college-page-exp-label">Address</span>
                            <span className="admin-panel-view-college-page-exp-value">{col.address || '—'}</span>
                          </div>

                          {/* Terms — always in expand (never in table) */}
                          <div className="admin-panel-view-college-page-expanded-item admin-panel-view-college-page-exp-full">
                            <span className="admin-panel-view-college-page-exp-label">Terms and Conditions</span>
                            <span className="admin-panel-view-college-page-exp-value" style={{ fontWeight: 400, color: '#555' }}>{col.termCondition || '—'}</span>
                          </div>

                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default ViewCollege;